'use client';

import { getBrowserFfmpegRuntime, resetBrowserFfmpegRuntime } from '@/lib/browser-gif-tools';
import {
  canvasToBlob,
  clamp,
  createCanvas,
  drawImageToCanvas,
  getBaseName,
  loadImageElementFromBlob,
  sanitizeHexColor,
} from '@/lib/browser-image-tools';
import { createZipBlob } from '@/lib/browser-png-tools';

type FfmpegRuntime = Awaited<ReturnType<typeof getBrowserFfmpegRuntime>>;

export type VideoMetadata = {
  width: number;
  height: number;
  durationMs: number;
  size: number;
  mimeType: string;
  approximateBitrateKbps: number | null;
};

export type VideoQualityPreset = 'balanced' | 'smaller' | 'aggressive';
export type VideoFitMode = 'contain' | 'cover';
export type VideoScaleMode = 'contain' | 'cover';
// ogg here means Vorbis in an Ogg container. libvorbis is already proven present in
// this wasm build: the webm container recipe below encodes audio with it, so this
// adds a format rather than a dependency.
export type VideoAudioOutputFormat = 'mp3' | 'wav' | 'm4a' | 'ogg';
export type SubtitleBackgroundStyle = 'none' | 'soft-box';
export type SubtitleFileType = 'srt' | 'vtt';

export type ExtractedVideoFrame = {
  index: number;
  name: string;
  blob: Blob;
};

// wmv, flv, ts and 3gp are here even though Chrome cannot play any of them. The
// file gate and the decoder are two different things: see probeVideoMetadataWithFfmpeg.
const acceptedVideoExtensionPattern =
  /\.(mp4|mov|m4v|webm|avi|mkv|mpeg|mpg|wmv|asf|flv|ts|m2ts|mts|3gp|3g2|ogv)$/i;

function normalizeFfmpegOutput(data: string | Uint8Array) {
  const bytes = data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy;
}

function getFileExtension(fileName: string, fallback = 'mp4') {
  const normalized = fileName.toLowerCase();

  if (normalized.endsWith('.mov')) {
    return 'mov';
  }

  if (normalized.endsWith('.m4v')) {
    return 'm4v';
  }

  if (normalized.endsWith('.webm')) {
    return 'webm';
  }

  if (normalized.endsWith('.avi')) {
    return 'avi';
  }

  if (normalized.endsWith('.mkv')) {
    return 'mkv';
  }

  if (normalized.endsWith('.mpeg') || normalized.endsWith('.mpg')) {
    return 'mpeg';
  }

  return normalized.split('.').pop() || fallback;
}

function formatSeconds(seconds: number) {
  return Math.max(0, seconds).toFixed(3);
}

function makeEven(value: number) {
  return Math.max(2, Math.round(value / 2) * 2);
}

function getCrfForPreset(preset: VideoQualityPreset) {
  if (preset === 'smaller') {
    return 31;
  }

  if (preset === 'aggressive') {
    return 35;
  }

  return 27;
}

function buildAtempoFilter(speedFactor: number) {
  const clamped = clamp(speedFactor || 1, 0.25, 4);
  const filters: string[] = [];
  let remaining = clamped;

  while (remaining < 0.5) {
    filters.push('atempo=0.5');
    remaining /= 0.5;
  }

  while (remaining > 2) {
    filters.push('atempo=2');
    remaining /= 2;
  }

  filters.push(`atempo=${remaining.toFixed(4)}`);
  return filters.join(',');
}

function buildConcatManifest(fileNames: string[]) {
  return `${fileNames.map((fileName) => `file '${fileName.replace(/'/g, "'\\''")}'`).join('\n')}\n`;
}

async function cleanupFiles(runtime: FfmpegRuntime, fileNames: string[]) {
  await Promise.allSettled(fileNames.map((fileName) => runtime.ffmpeg.deleteFile(fileName)));
}

async function readBlobFromFile(runtime: FfmpegRuntime, fileName: string, mimeType: string) {
  const outputData = await runtime.ffmpeg.readFile(fileName);
  const outputBytes = normalizeFfmpegOutput(outputData);
  return new Blob([outputBytes], { type: mimeType });
}

function buildRangeArgs(startMs?: number, endMs?: number) {
  if (typeof startMs !== 'number' && typeof endMs !== 'number') {
    return [] as string[];
  }

  const normalizedStart = Math.max(0, startMs ?? 0);
  const durationSeconds =
    typeof endMs === 'number' ? Math.max(0.05, (endMs - normalizedStart) / 1000) : null;
  const args = ['-ss', formatSeconds(normalizedStart / 1000)];

  if (durationSeconds !== null) {
    args.push('-t', formatSeconds(durationSeconds));
  }

  return args;
}

function buildAudioCodecArgs(includeAudio: boolean, bitrateKbps = 128) {
  if (!includeAudio) {
    return ['-an'];
  }

  return ['-c:a', 'aac', '-b:a', `${clamp(Math.round(bitrateKbps), 64, 320)}k`];
}

function buildMp4EncodeArgs(crf: number) {
  return [
    '-c:v',
    'libx264',
    '-preset',
    'veryfast',
    '-crf',
    String(clamp(Math.round(crf), 18, 40)),
    '-pix_fmt',
    'yuv420p',
    '-movflags',
    '+faststart',
  ];
}

function getResizeFilter(params: {
  width: number;
  height: number;
  fitMode: VideoFitMode;
  background: string;
}) {
  const width = makeEven(params.width);
  const height = makeEven(params.height);

  if (params.fitMode === 'cover') {
    return `scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height}`;
  }

  return `scale=${width}:${height}:force_original_aspect_ratio=decrease,pad=${width}:${height}:(ow-iw)/2:(oh-ih)/2:${sanitizeHexColor(params.background)}`;
}

async function runVideoToMp4(params: {
  file: File;
  outputName?: string;
  filters?: string[];
  includeAudio?: boolean;
  audioBitrateKbps?: number;
  startMs?: number;
  endMs?: number;
  extraArgs?: string[];
  crf?: number;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputName = params.outputName ?? 'output.mp4';
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));
    const command = [
      ...buildRangeArgs(params.startMs, params.endMs),
      '-i',
      inputName,
      '-map',
      '0:v:0',
      ...(params.includeAudio === false ? [] : ['-map', '0:a?']),
    ];

    if (params.filters?.length) {
      command.push('-vf', params.filters.join(','));
    }

    if (params.extraArgs?.length) {
      command.push(...params.extraArgs);
    }

    command.push(
      ...buildMp4EncodeArgs(params.crf ?? 27),
      ...buildAudioCodecArgs(params.includeAudio !== false, params.audioBitrateKbps),
      outputName,
    );

    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The local MP4 export could not be generated in this browser.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export function isVideoFile(file: File) {
  return file.type.startsWith('video/') || acceptedVideoExtensionPattern.test(file.name);
}

// ---- progress + cancel -------------------------------------------------------
//
// ffmpeg.wasm emits `progress` as a 0..1 ratio derived from the input duration.
// One listener per conversion; the component attaches before exec and detaches in
// its finally block.
export async function attachVideoProgressListener(listener: (ratio: number) => void) {
  const runtime = await getBrowserFfmpegRuntime();
  const handler = ({ progress }: { progress: number }) => {
    if (Number.isFinite(progress)) {
      listener(clamp(progress, 0, 1));
    }
  };

  runtime.ffmpeg.on('progress', handler);
  return () => runtime.ffmpeg.off('progress', handler);
}

// terminate() kills the wasm worker mid-exec, which is the only way to stop a
// running conversion. The instance is unusable afterwards, so the memoized runtime
// is dropped too and the next run boots fresh.
export function cancelActiveVideoWork() {
  resetBrowserFfmpegRuntime();
}

// After a failed conversion the core may be in an aborted state (OOM and codec
// crashes both look like a generic non-zero exit). Resetting is cheap because the
// core download is HTTP-cached, and it turns "every following run fails until a
// full page reload" into "the next run just works".
export function recoverVideoRuntimeAfterFailure() {
  resetBrowserFfmpegRuntime();
}

// ---- stream probe ------------------------------------------------------------
//
// `-i` with no output prints the stream table to the log and exits non-zero; the
// exit code is meaningless here. Only the FIRST video/audio stream matters because
// every command below maps 0:v:0 and 0:a?.
async function probeInputCodecs(runtime: FfmpegRuntime, inputName: string) {
  let log = '';
  const collect = ({ message }: { message: string }) => {
    log += `${message}\n`;
  };

  runtime.ffmpeg.on('log', collect);

  try {
    await runtime.ffmpeg.exec(['-i', inputName]);
  } finally {
    runtime.ffmpeg.off('log', collect);
  }

  const video = /Stream #0:\d+[^\n]*?: Video: (\w+)/.exec(log)?.[1]?.toLowerCase() ?? null;
  const audio = /Stream #0:\d+[^\n]*?: Audio: (\w+)/.exec(log)?.[1]?.toLowerCase() ?? null;
  return { video, audio };
}

// MP4 can carry these without re-encoding. mp3 is legal in MP4 and every modern
// player reads it; keeping it beats a lossy AAC re-encode.
const MP4_COPYABLE_AUDIO = new Set(['aac', 'mp3']);

// Ask ffmpeg what the file is when the browser refuses to say.
//
// `-i` with no output exits non-zero and prints the stream table to the log, which
// is exactly what we want and the reason the exit code is ignored here.
async function probeVideoMetadataWithFfmpeg(file: File): Promise<VideoMetadata> {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `probe.${getFileExtension(file.name, 'bin')}`;
  let log = '';
  const collect = ({ message }: { message: string }) => {
    log += `${message}\n`;
  };

  try {
    runtime.ffmpeg.on('log', collect);
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(file));
    await runtime.ffmpeg.exec(['-i', inputName]);

    const size = /,\s(\d{2,5})x(\d{2,5})[\s,]/.exec(log);
    const duration = /Duration:\s(\d+):(\d{2}):(\d{2})\.(\d{2})/.exec(log);

    if (!size) {
      throw new Error('This video format could not be read in your browser.');
    }

    const durationMs = duration
      ? (Number(duration[1]) * 3600 + Number(duration[2]) * 60 + Number(duration[3])) * 1000 +
        Number(duration[4]) * 10
      : 0;

    return {
      width: Number(size[1]),
      height: Number(size[2]),
      durationMs,
      size: file.size,
      mimeType: file.type,
      approximateBitrateKbps:
        durationMs > 0
          ? Math.max(1, Math.round((file.size * 8) / (durationMs / 1000) / 1000))
          : null,
    };
  } finally {
    runtime.ffmpeg.off('log', collect);
    await cleanupFiles(runtime, [inputName]);
  }
}

// The <video> element is tried first because it is instant and needs no wasm. It is
// also the reason WMV, FLV, MPEG-PS and TS were rejected before ffmpeg ever saw
// them: the gate was Chrome's decoder, not ours. Falling back to an ffmpeg probe
// costs one wasm load, and only for the formats the browser cannot read anyway.
export async function readVideoMetadata(file: File): Promise<VideoMetadata> {
  if (!isVideoFile(file)) {
    throw new Error('Choose one video file for this tool.');
  }

  const objectUrl = URL.createObjectURL(file);

  try {
    const video = await new Promise<HTMLVideoElement>((resolve, reject) => {
      const element = document.createElement('video');
      element.preload = 'metadata';
      element.muted = true;
      element.playsInline = true;
      element.onloadedmetadata = () => resolve(element);
      element.onerror = () => reject(new Error('BROWSER_CANNOT_DECODE'));
      element.src = objectUrl;
    }).catch(async (error: unknown) => {
      if (error instanceof Error && error.message === 'BROWSER_CANNOT_DECODE') {
        return null;
      }

      throw error;
    });

    if (!video) {
      URL.revokeObjectURL(objectUrl);
      return await probeVideoMetadataWithFfmpeg(file);
    }

    const durationSeconds = Number.isFinite(video.duration) ? video.duration : 0;
    const approximateBitrateKbps =
      durationSeconds > 0
        ? Math.max(1, Math.round((file.size * 8) / durationSeconds / 1000))
        : null;

    return {
      width: video.videoWidth || 0,
      height: video.videoHeight || 0,
      durationMs: Math.max(0, Math.round(durationSeconds * 1000)),
      size: file.size,
      mimeType: file.type,
      approximateBitrateKbps,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function extractVideoFramePng(file: File, timeMs: number) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const video = await new Promise<HTMLVideoElement>((resolve, reject) => {
      const element = document.createElement('video');
      element.preload = 'auto';
      element.muted = true;
      element.playsInline = true;
      element.onloadedmetadata = () => resolve(element);
      element.onerror = () => reject(new Error('This video frame could not be loaded.'));
      element.src = objectUrl;
    });

    await new Promise<void>((resolve, reject) => {
      let finished = false;
      let timeoutId: number | null = null;

      const finalize = (callback: () => void) => {
        if (finished) {
          return;
        }

        finished = true;

        if (timeoutId !== null) {
          window.clearTimeout(timeoutId);
        }

        video.removeEventListener('seeked', handleSeeked);
        callback();
      };

      const handleSeeked = () => {
        finalize(resolve);
      };

      video.addEventListener('seeked', handleSeeked, { once: true });
      video.currentTime = Math.max(0, Math.min((video.duration || 0) - 0.05, timeMs / 1000));
      timeoutId = window.setTimeout(() => {
        finalize(() => reject(new Error('The browser could not seek to this video frame.')));
      }, 6000);
    });

    const canvas = createCanvas(video.videoWidth || 1, video.videoHeight || 1);
    const context = canvas.getContext('2d');

    if (!context) {
      throw new Error('The browser could not prepare a preview canvas for this frame.');
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (value) => (value ? resolve(value) : reject(new Error('The frame could not be exported.'))),
        'image/png',
      ),
    );

    return blob;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function compressVideo(params: {
  file: File;
  preset: VideoQualityPreset;
  fpsCap: number | null;
  qualityTier: 'standard' | 'lighter' | 'smallest';
}) {
  const filters = [`scale=trunc(iw/2)*2:trunc(ih/2)*2`];

  if (params.fpsCap) {
    filters.unshift(`fps=${clamp(params.fpsCap, 6, 60)}`);
  }

  const baseCrf = getCrfForPreset(params.preset);
  const crf =
    params.qualityTier === 'smallest'
      ? baseCrf + 4
      : params.qualityTier === 'lighter'
        ? baseCrf + 2
        : baseCrf;

  return runVideoToMp4({
    file: params.file,
    filters,
    includeAudio: true,
    crf,
  });
}

export async function resizeVideo(params: {
  file: File;
  width: number;
  height: number;
  fitMode: VideoFitMode;
  background: string;
}) {
  return runVideoToMp4({
    file: params.file,
    filters: [getResizeFilter(params)],
    includeAudio: true,
    crf: 25,
  });
}

export async function trimVideo(params: { file: File; startMs: number; endMs: number }) {
  return runVideoToMp4({
    file: params.file,
    startMs: params.startMs,
    endMs: params.endMs,
    includeAudio: true,
    crf: 24,
  });
}

export async function cropVideo(params: {
  file: File;
  x: number;
  y: number;
  width: number;
  height: number;
}) {
  const width = makeEven(params.width);
  const height = makeEven(params.height);
  const x = Math.max(0, Math.round(params.x));
  const y = Math.max(0, Math.round(params.y));

  return runVideoToMp4({
    file: params.file,
    filters: [`crop=${width}:${height}:${x}:${y}`],
    includeAudio: true,
    crf: 24,
  });
}

// Remux first, encode only when the codecs force it. The overwhelmingly common
// "mkv/mov/avi to mp4" case is an H.264 stream in the wrong container; `-c copy`
// rewraps it in seconds, losslessly, where the old unconditional libx264 pass took
// minutes and made the picture worse. The encode path stays as the fallback for
// real transcodes (wmv, mpeg, flv sources) and for when the copy attempt fails.
export async function normalizeVideoToMp4(params: { file: File; preset: VideoQualityPreset }) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputName = 'output.mp4';
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));
    const codecs = await probeInputCodecs(runtime, inputName);

    if (codecs.video === 'h264' && (!codecs.audio || MP4_COPYABLE_AUDIO.has(codecs.audio))) {
      const remuxExit = await runtime.ffmpeg.exec([
        '-i',
        inputName,
        '-map',
        '0:v:0',
        '-map',
        '0:a?',
        '-c',
        'copy',
        '-movflags',
        '+faststart',
        outputName,
      ]);

      if (remuxExit === 0) {
        return await readBlobFromFile(runtime, outputName, 'video/mp4');
      }

      await Promise.allSettled([runtime.ffmpeg.deleteFile(outputName)]);
    }

    const command = [
      '-i',
      inputName,
      '-map',
      '0:v:0',
      '-map',
      '0:a?',
      ...buildMp4EncodeArgs(getCrfForPreset(params.preset)),
      ...buildAudioCodecArgs(true),
      outputName,
    ];
    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The local MP4 export could not be generated in this browser.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

// ---- container conversion (mp4 out to mov / mkv / webm / avi) ----------------
//
// runVideoToMp4 hard-codes H.264 + AAC in an .mp4, which is right for everything that
// converts INTO mp4. Converting OUT of it needs a different codec pair per container,
// so this is a sibling runner rather than a parameter on that one.
//
// The codec choices are constrained by what the @ffmpeg/core 0.12.9 wasm build actually
// ships (verified by running -encoders/-muxers against the real runtime, not assumed):
//   mov, mkv  H.264 + AAC, the same pair as mp4, only the container changes
//   webm      VP8 + Vorbis. VP8 rather than VP9 on purpose: VP9 in WebAssembly is
//             several times slower for a barely smaller file, and this runs on the
//             visitor's own CPU.
//   avi       MPEG-4 part 2 + MP3, because AVI predates H.264 and players that still
//             want an AVI are the ones least likely to decode H.264 inside it.
export type VideoContainerTarget = 'mov' | 'mkv' | 'webm' | 'avi';

const containerRecipes: Record<
  VideoContainerTarget,
  { extension: string; mimeType: string; format: string; videoArgs: string[]; audioArgs: string[] }
> = {
  mov: {
    extension: 'mov',
    mimeType: 'video/quicktime',
    format: 'mov',
    videoArgs: ['-c:v', 'libx264', '-preset', 'veryfast', '-pix_fmt', 'yuv420p'],
    audioArgs: ['-c:a', 'aac', '-b:a', '192k'],
  },
  mkv: {
    extension: 'mkv',
    mimeType: 'video/x-matroska',
    format: 'matroska',
    videoArgs: ['-c:v', 'libx264', '-preset', 'veryfast', '-pix_fmt', 'yuv420p'],
    audioArgs: ['-c:a', 'aac', '-b:a', '192k'],
  },
  webm: {
    extension: 'webm',
    mimeType: 'video/webm',
    format: 'webm',
    videoArgs: ['-c:v', 'libvpx', '-b:v', '1M', '-deadline', 'realtime', '-cpu-used', '5'],
    audioArgs: ['-c:a', 'libvorbis', '-b:a', '160k'],
  },
  avi: {
    extension: 'avi',
    mimeType: 'video/x-msvideo',
    format: 'avi',
    videoArgs: ['-c:v', 'mpeg4', '-vtag', 'DIVX', '-qscale:v', '5'],
    audioArgs: ['-c:a', 'libmp3lame', '-b:a', '192k'],
  },
};

export function getVideoContainerExtension(target: VideoContainerTarget) {
  return containerRecipes[target].extension;
}

export async function convertVideoToContainer(params: {
  file: File;
  target: VideoContainerTarget;
  crf?: number;
}) {
  const recipe = containerRecipes[params.target];
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputName = `output.${recipe.extension}`;
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));

    // MOV and MKV hold H.264 + AAC natively, so when the source already carries that
    // pair (the normal case for an MP4 input) this really is the re-wrap the page
    // promises: stream copy, lossless, near-instant. WebM and AVI need different
    // codecs, so they always transcode.
    let videoArgs = recipe.videoArgs;
    let audioArgs = recipe.audioArgs;

    if (params.target === 'mov' || params.target === 'mkv') {
      const codecs = await probeInputCodecs(runtime, inputName);

      if (codecs.video === 'h264') {
        videoArgs = ['-c:v', 'copy'];
      }

      if (codecs.audio === 'aac') {
        audioArgs = ['-c:a', 'copy'];
      }
    }

    const command = ['-i', inputName, '-map', '0:v:0', '-map', '0:a?', ...videoArgs];

    // libvpx and mpeg4 use their own rate control, so CRF only applies to the x264 pair.
    if (videoArgs.includes('libx264')) {
      command.push('-crf', String(clamp(Math.round(params.crf ?? 24), 18, 40)));
    }

    if (params.target === 'mov') {
      command.push('-movflags', '+faststart');
    }

    command.push(...audioArgs, '-f', recipe.format, outputName);

    let exitCode = await runtime.ffmpeg.exec(command);

    // A failed stream copy (odd H.264 flavors do exist) falls back to the encode.
    if (exitCode !== 0 && (videoArgs[1] === 'copy' || audioArgs[1] === 'copy')) {
      await Promise.allSettled([runtime.ffmpeg.deleteFile(outputName)]);
      const encodeCommand = ['-i', inputName, '-map', '0:v:0', '-map', '0:a?', ...recipe.videoArgs];

      if (recipe.videoArgs.includes('libx264')) {
        encodeCommand.push('-crf', String(clamp(Math.round(params.crf ?? 24), 18, 40)));
      }

      encodeCommand.push(...recipe.audioArgs, '-f', recipe.format, outputName);
      exitCode = await runtime.ffmpeg.exec(encodeCommand);
    }

    if (exitCode !== 0) {
      throw new Error(
        `The local ${recipe.extension.toUpperCase()} export could not be generated in this browser.`,
      );
    }

    return await readBlobFromFile(runtime, outputName, recipe.mimeType);
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function muteVideo(file: File) {
  return runVideoToMp4({
    file,
    includeAudio: false,
    crf: 24,
  });
}

export async function changeVideoSpeed(params: { file: File; speedFactor: number }) {
  const factor = clamp(params.speedFactor || 1, 0.25, 4);

  return runVideoToMp4({
    file: params.file,
    filters: [`setpts=${(1 / factor).toFixed(5)}*PTS`],
    includeAudio: true,
    extraArgs: ['-af', buildAtempoFilter(factor)],
    crf: 24,
  });
}

export async function rotateVideo(params: { file: File; rotation: -90 | 90 | 180 }) {
  const filters =
    params.rotation === 90
      ? ['transpose=1']
      : params.rotation === -90
        ? ['transpose=2']
        : ['transpose=2,transpose=2'];

  return runVideoToMp4({
    file: params.file,
    filters,
    includeAudio: true,
    crf: 24,
  });
}

export async function extractAudioFromVideo(params: {
  file: File;
  startMs: number;
  endMs: number;
  format: VideoAudioOutputFormat;
  bitrateKbps: number;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputName = `output.${params.format}`;
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));

    const command = [...buildRangeArgs(params.startMs, params.endMs), '-i', inputName, '-vn'];

    if (params.format === 'wav') {
      command.push('-acodec', 'pcm_s16le', outputName);
    } else if (params.format === 'm4a') {
      command.push('-c:a', 'aac', '-b:a', `${clamp(params.bitrateKbps, 64, 320)}k`, outputName);
    } else if (params.format === 'ogg') {
      command.push(
        '-c:a',
        'libvorbis',
        '-b:a',
        `${clamp(params.bitrateKbps, 64, 320)}k`,
        outputName,
      );
    } else {
      command.push(
        '-c:a',
        'libmp3lame',
        '-b:a',
        `${clamp(params.bitrateKbps, 64, 320)}k`,
        outputName,
      );
    }

    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The audio could not be extracted from this video locally.');
    }

    return await readBlobFromFile(
      runtime,
      outputName,
      params.format === 'wav'
        ? 'audio/wav'
        : params.format === 'm4a'
          ? 'audio/mp4'
          : params.format === 'ogg'
            ? 'audio/ogg'
            : 'audio/mpeg',
    );
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export type VideoFrameFormat = 'png' | 'jpg';

// ffmpeg always writes PNG frames here. The mjpeg encoder in the @ffmpeg/core 0.12.9 wasm
// build crashes at runtime ("memory access out of bounds") mid-encode, so JPG is produced
// by decoding each PNG frame onto a canvas and re-encoding as JPEG, which is deterministic
// and reuses the same canvas path the rest of the site relies on. canvas.toBlob is
// trustworthy for image/jpeg (unlike image/webp, which WebKit can silently swap to PNG).
async function recodePngBlobToJpeg(pngBlob: Blob) {
  const image = await loadImageElementFromBlob(pngBlob);
  const { canvas } = drawImageToCanvas(image, image.naturalWidth, image.naturalHeight);
  return canvasToBlob(canvas, 'image/jpeg', 0.92);
}

export async function extractVideoFramesToZip(params: {
  file: File;
  startMs: number;
  endMs: number;
  fps: number;
  format?: VideoFrameFormat;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const asJpeg = params.format === 'jpg';
  const outExtension = asJpeg ? 'jpg' : 'png';
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputPattern = 'frame-%04d.png';
  const cleanupTargets = [inputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));

    const command = [
      ...buildRangeArgs(params.startMs, params.endMs),
      '-i',
      inputName,
      '-vf',
      `fps=${clamp(params.fps, 1, 30)}`,
      outputPattern,
    ];
    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The video frames could not be extracted locally.');
    }

    const frames: ExtractedVideoFrame[] = [];

    for (let index = 1; index < 10000; index += 1) {
      const pngName = `frame-${String(index).padStart(4, '0')}.png`;

      try {
        const outputData = await runtime.ffmpeg.readFile(pngName);
        const bytes = normalizeFfmpegOutput(outputData);
        const pngBlob = new Blob([bytes], { type: 'image/png' });
        const blob = asJpeg ? await recodePngBlobToJpeg(pngBlob) : pngBlob;
        frames.push({
          index,
          name: `frame-${String(index).padStart(4, '0')}.${outExtension}`,
          blob,
        });
        cleanupTargets.push(pngName);
      } catch {
        break;
      }
    }

    const zipBlob = await createZipBlob(
      frames.map((frame) => ({
        name: frame.name,
        data: frame.blob,
      })),
    );

    return { zipBlob, frameCount: frames.length };
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

const SUBTITLE_FONT_DIR = '/fonts';
const SUBTITLE_FONT_NAME = 'Noto Sans SemiBold';
const SUBTITLE_FONT_URL = '/fonts/subtitles/NotoSans-SemiBold.ttf';

/** Noto Sans (Latin, Cyrillic, Greek; OFL) copied into the ffmpeg file system once per runtime. */
async function ensureSubtitleFont(runtime: Awaited<ReturnType<typeof getBrowserFfmpegRuntime>>) {
  const target = `${SUBTITLE_FONT_DIR}/NotoSans-SemiBold.ttf`;
  try {
    await runtime.ffmpeg.readFile(target);
    return;
  } catch {
    // Not there yet.
  }
  try {
    await runtime.ffmpeg.createDir(SUBTITLE_FONT_DIR);
  } catch {
    // Already exists.
  }
  const response = await fetch(SUBTITLE_FONT_URL);
  if (!response.ok)
    throw new Error('The caption font could not be loaded. Check your connection and try again.');
  await runtime.ffmpeg.writeFile(target, new Uint8Array(await response.arrayBuffer()));
}

export async function burnSubtitlesIntoVideo(params: {
  file: File;
  subtitleFile: File;
  subtitleType: SubtitleFileType;
  sizePreset: 'small' | 'medium' | 'large';
  bottomOffset: number;
  backgroundStyle: SubtitleBackgroundStyle;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const subtitleName = `captions.${params.subtitleType}`;
  const outputName = 'subtitled.mp4';
  const cleanupTargets = [inputName, subtitleName, outputName];
  const fontSize = params.sizePreset === 'small' ? 18 : params.sizePreset === 'large' ? 30 : 24;
  const forceStyle =
    params.backgroundStyle === 'soft-box'
      ? `Fontsize=${fontSize},MarginV=${Math.max(12, Math.round(params.bottomOffset))},BorderStyle=3,Outline=0,Shadow=0,BackColour=&H66000000`
      : `Fontsize=${fontSize},MarginV=${Math.max(12, Math.round(params.bottomOffset))}`;

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));
    await runtime.ffmpeg.writeFile(subtitleName, await runtime.fetchFile(params.subtitleFile));
    // ffmpeg.wasm has no system fonts, and libass with no font renders nothing while ffmpeg
    // still exits 0: the video came out with no captions at all (2026-10-08). Ship one.
    await ensureSubtitleFont(runtime);

    const exitCode = await runtime.ffmpeg.exec([
      '-i',
      inputName,
      '-vf',
      `subtitles=${subtitleName}:fontsdir=${SUBTITLE_FONT_DIR}:force_style='FontName=${SUBTITLE_FONT_NAME},${forceStyle}'`,
      ...buildMp4EncodeArgs(24),
      ...buildAudioCodecArgs(true, 128),
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('The subtitles could not be burned into this video locally.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function loopVideo(params: {
  file: File;
  startMs: number;
  endMs: number;
  repeatCount: number;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const clipName = 'clip.mp4';
  const manifestName = 'segments.txt';
  const outputName = 'loop.mp4';
  const cleanupTargets = [inputName, clipName, manifestName, outputName];
  const repeatCount = clamp(Math.round(params.repeatCount) || 1, 1, 20);

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));
    const clipExitCode = await runtime.ffmpeg.exec([
      ...buildRangeArgs(params.startMs, params.endMs),
      '-i',
      inputName,
      '-map',
      '0:v:0',
      '-map',
      '0:a?',
      ...buildMp4EncodeArgs(24),
      ...buildAudioCodecArgs(true, 128),
      clipName,
    ]);

    if (clipExitCode !== 0) {
      throw new Error('The loop clip could not be prepared locally.');
    }

    const manifest = buildConcatManifest(new Array(repeatCount).fill(clipName));
    await runtime.ffmpeg.writeFile(manifestName, manifest);
    const outputExitCode = await runtime.ffmpeg.exec([
      '-f',
      'concat',
      '-safe',
      '0',
      '-i',
      manifestName,
      '-c',
      'copy',
      '-movflags',
      '+faststart',
      outputName,
    ]);

    if (outputExitCode !== 0) {
      throw new Error('The looped video could not be exported locally.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function blurVideoBackground(params: {
  file: File;
  canvasWidth: number;
  canvasHeight: number;
  blurStrength: number;
  scaleMode: VideoScaleMode;
}) {
  const width = makeEven(params.canvasWidth);
  const height = makeEven(params.canvasHeight);
  const blur = clamp(Math.round(params.blurStrength) || 18, 4, 40);
  const fgScale =
    params.scaleMode === 'cover'
      ? `scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height}`
      : `scale=${width}:${height}:force_original_aspect_ratio=decrease`;
  // The filtergraph output must be labeled and mapped explicitly: runVideoToMp4's
  // `-map 0:v:0` alongside an unlabeled filter_complex output makes ffmpeg reject
  // the graph as unconnected, which is why this tool produced no output at all.
  const filter = [
    `[0:v]scale=${width}:${height}:force_original_aspect_ratio=increase,crop=${width}:${height},boxblur=${blur}:1[bg]`,
    `[0:v]${fgScale}[fg]`,
    `[bg][fg]overlay=(W-w)/2:(H-h)/2[out]`,
  ].join(';');

  const runtime = await getBrowserFfmpegRuntime();
  const inputName = `input.${getFileExtension(params.file.name, 'mp4')}`;
  const outputName = 'output.mp4';
  const cleanupTargets = [inputName, outputName];

  try {
    await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(params.file));
    const command = [
      '-i',
      inputName,
      '-filter_complex',
      filter,
      '-map',
      '[out]',
      '-map',
      '0:a?',
      ...buildMp4EncodeArgs(24),
      ...buildAudioCodecArgs(true),
      outputName,
    ];
    const exitCode = await runtime.ffmpeg.exec(command);

    if (exitCode !== 0) {
      throw new Error('The local MP4 export could not be generated in this browser.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

/** Duration of an audio file from the browser's own decoder, or null if it cannot tell. */
function readAudioDuration(file: File): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const audio = new Audio();
    const done = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };
    audio.preload = 'metadata';
    audio.onloadedmetadata = () =>
      done(Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : null);
    audio.onerror = () => done(null);
    setTimeout(() => done(null), 8000);
    audio.src = url;
  });
}

// ---- audio + still image -> mp4 --------------------------------------------
//
// "mp3 to mp4" is 22,200/mo and it is not really a conversion: an MP3 has no
// picture, so one has to be invented. Every competitor uploads the file; doing it
// locally means the audio never leaves the machine, which matters more here than
// usual because this is the tool people reach for with unreleased music.
//
// A cover image is always required, and the caller synthesises one on a canvas
// when the visitor does not supply it. The obvious alternative, `-f lavfi -i
// color=...`, would put the whole tool at the mercy of whether the lavfi input
// device survived into this wasm build, for a background we can draw ourselves in
// three lines. One code path, no runtime gamble.
//
// Frame rate is deliberately low. The picture never changes, so every frame after
// the first is a near-empty P-frame; at 30fps a four-minute track would be 7,200
// frames of pointless work on the visitor's own CPU. Five is high enough for every
// player that matters and finishes many times faster.
//
// yuv420p needs even dimensions, hence the scale filter: an odd-width cover would
// otherwise fail the encode outright rather than degrade.
export async function convertAudioToVideo(params: {
  audioFile: File;
  imageFile: File | Blob;
  imageName?: string;
  frameRate?: number;
  audioBitrateKbps?: number;
}) {
  const runtime = await getBrowserFfmpegRuntime();
  const audioName = `audio.${getFileExtension(params.audioFile.name, 'mp3')}`;
  const imageName = `cover.${getFileExtension(params.imageName ?? 'cover.png', 'png')}`;
  const outputName = 'output.mp4';
  const cleanupTargets = [audioName, imageName, outputName];
  const frameRate = Math.min(30, Math.max(1, Math.round(params.frameRate ?? 5)));

  try {
    await runtime.ffmpeg.writeFile(audioName, await runtime.fetchFile(params.audioFile));
    await runtime.ffmpeg.writeFile(imageName, await runtime.fetchFile(params.imageFile));
    // With a looped still image, -shortest alone overshoots by the encoder's buffer: a 3 s MP3
    // became a 5.8 s MP4 ending in silence (2026-10-08). Cap the output at the audio length.
    const audioSeconds = await readAudioDuration(params.audioFile);

    const exitCode = await runtime.ffmpeg.exec([
      '-loop',
      '1',
      '-framerate',
      String(frameRate),
      '-i',
      imageName,
      '-i',
      audioName,
      '-map',
      '0:v:0',
      '-map',
      '1:a:0',
      '-vf',
      'scale=trunc(iw/2)*2:trunc(ih/2)*2',
      '-c:v',
      'libx264',
      '-preset',
      'veryfast',
      '-pix_fmt',
      'yuv420p',
      '-r',
      String(frameRate),
      '-c:a',
      'aac',
      '-b:a',
      `${Math.min(320, Math.max(96, Math.round(params.audioBitrateKbps ?? 192)))}k`,
      '-shortest',
      ...(audioSeconds ? ['-t', audioSeconds.toFixed(3)] : []),
      '-movflags',
      '+faststart',
      outputName,
    ]);

    if (exitCode !== 0) {
      throw new Error('The local MP4 export could not be generated in this browser.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

export async function mergeVideos(files: File[]) {
  const runtime = await getBrowserFfmpegRuntime();
  const metadatas = await Promise.all(files.map((file) => readVideoMetadata(file)));
  const targetWidth = makeEven(metadatas[0]?.width || 1280);
  const targetHeight = makeEven(metadatas[0]?.height || 720);
  const cleanupTargets: string[] = [];
  const normalizedNames: string[] = [];
  const manifestName = 'inputs.txt';
  const outputName = 'merged.mp4';
  cleanupTargets.push(manifestName, outputName);

  try {
    for (let index = 0; index < files.length; index += 1) {
      const inputName = `input-${String(index + 1).padStart(3, '0')}.${getFileExtension(files[index].name, 'mp4')}`;
      const normalizedName = `normalized-${String(index + 1).padStart(3, '0')}.mp4`;
      normalizedNames.push(normalizedName);
      cleanupTargets.push(inputName, normalizedName);
      await runtime.ffmpeg.writeFile(inputName, await runtime.fetchFile(files[index]));
      const exitCode = await runtime.ffmpeg.exec([
        '-i',
        inputName,
        '-map',
        '0:v:0',
        '-map',
        '0:a?',
        '-vf',
        getResizeFilter({
          width: targetWidth,
          height: targetHeight,
          fitMode: 'contain',
          background: '#000000',
        }),
        ...buildMp4EncodeArgs(24),
        ...buildAudioCodecArgs(true, 128),
        normalizedName,
      ]);

      if (exitCode !== 0) {
        throw new Error('One of the videos could not be normalized for merge locally.');
      }
    }

    await runtime.ffmpeg.writeFile(manifestName, buildConcatManifest(normalizedNames));
    const concatExitCode = await runtime.ffmpeg.exec([
      '-f',
      'concat',
      '-safe',
      '0',
      '-i',
      manifestName,
      '-c',
      'copy',
      '-movflags',
      '+faststart',
      outputName,
    ]);

    if (concatExitCode !== 0) {
      throw new Error('The selected videos could not be merged locally.');
    }

    return await readBlobFromFile(runtime, outputName, 'video/mp4');
  } finally {
    await cleanupFiles(runtime, cleanupTargets);
  }
}

// The extension list must cover everything acceptedVideoExtensionPattern accepts:
// wmv/flv/ts files are often untyped or oddly typed by the OS, and without their
// extensions here the native picker greys out exactly the files the ffmpeg probe
// fallback exists to convert.
export const acceptedVideoMimeTypes =
  'video/mp4,video/webm,video/quicktime,video/x-m4v,video/mpeg,video/avi,video/x-matroska,video/mp2t,video/x-ms-wmv,video/x-flv,video/3gpp,video/*,.mp4,.mov,.m4v,.webm,.avi,.mkv,.mpeg,.mpg,.wmv,.asf,.flv,.ts,.m2ts,.mts,.3gp,.3g2,.ogv';

export { createZipBlob, getBaseName };
